import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { BsChevronDown, BsChevronRight } from "react-icons/bs";
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';


const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  color: theme.palette.text.secondary,
  borderRadius: theme.shape.borderRadius,
  paddingRight: 4
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  color: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
  nodeMatch: PropTypes.func,
  menus: PropTypes.object,
  setMenus: PropTypes.func,
};

function NavItem({ item, active, nodeMatch, menus, setMenus }) {
  const theme = useTheme();
  const { id, title, path, icon, info, children } = item;
  const isActiveRoot = nodeMatch(item, active);
  const [open, setOpen] = useState(isActiveRoot || menus[item.id]);

  useEffect(() => {
    setMenus({...menus, [item.id]: open})
  }, [open]);

  useEffect(() => { 
    window.localStorage.setItem( 'menuAdmin', JSON.stringify(menus));
  }, [menus]);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const activeRootStyle = {
    color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
  };

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          <ListItemText disableTypography primary={title} />
          {info && info}
          {open ? <BsChevronRight /> : <BsChevronDown /> }
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((item) => {
              const { id, title, path } = item;
              const isActiveSub = active(path);

              return (
                <ListItemStyle
                  key={id}
                  component={RouterLink}
                  to={path}
                  sx={{
                    ...(isActiveSub && activeSubStyle),
                  }}
                >
                  <ListItemIconStyle>
                    <Box
                      component="span"
                      sx={{
                        display: 'none'
                      }}
                    />
                  </ListItemIconStyle>
                  <ListItemText disableTypography primary={title} />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle),
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      <ListItemText disableTypography primary={title} />
      {info && info}
    </ListItemStyle>
  );
}

NavSection.propTypes = {
  navigation: PropTypes.array,
};

export default function NavSection({ navigation, ...other }) {
  const { pathname } = useLocation();

  const collapseMenu = navigation.reduce((acc, cur) => {
    acc[cur.id] = cur.collapse;
    return acc;
  }, {})

  const loadedMenus = window.localStorage.getItem('menuAdmin') ? 
    JSON.parse(window.localStorage.getItem('menuAdmin')) : collapseMenu;
  const [menus, setMenus] = useState(loadedMenus);

  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  const nodeMatch = (item, active) => {
    const isActiveRoot = active(item.path)

    if(isActiveRoot) {
      return true;
    }
    else if(!isActiveRoot) {
      if(item.children) {
        const _filtered = item.children.filter((item) => active(item.path))
        if(_filtered.length > 0) {
          return true;
        }
      }
    }
    else {
      return false;
    }
  }

  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {navigation.map((item) => (
          <NavItem 
            key={item.title} 
            item={item} 
            active={match} 
            nodeMatch={nodeMatch} 
            menus={menus} 
            setMenus={setMenus} />
        ))}
      </List>
    </Box>
  );
}
